import { Box, TextField, InputAdornment, styled } from '@mui/material';
import { forwardRef } from 'react';

import Backspace from 'src/icons/Backspace';
import Search from 'src/icons/Search';
import PenSqure from 'src/icons/PenSqure';
import Book from 'src/icons/Book';
import Link from 'src/icons/Link';

import type { ChangeEvent } from 'react';

type CustomInputFieldProps = {
    onChange?: (value: string) => void;
    value: string;
    name?: string;
    placeholder?: string;
    disabled?: boolean;
    icon?: keyof typeof IconComponentMapper;
    multiline?: boolean;
    rows?: number;
    maxLength?: number;
};

const IconComponentMapper = {
    search: <Search />,
    edit: <PenSqure />,
    book: <Book />,
    link: <Link />,
} as const;

const StyledTextField = styled(TextField)(({ theme }) => ({
    '&.MuiTextField-root': { borderRadius: '16px' },
    backgroundColor: '#fff',
    // --- Стиль для текста когда есть значение ---
    '& .MuiInputBase-root:not(.Mui-focused) .MuiInputBase-input:not(:placeholder-shown)': {
        color: '#000000',
        opacity: 1,
    },
    // --- Стили для обводки активного инпута ---
    '& .MuiOutlinedInput-root': {
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
        },
    },
}));

const CustomInputField = forwardRef<HTMLInputElement, CustomInputFieldProps>(
    (
        {
            onChange,
            value,
            name = '',
            placeholder = '',
            disabled = false,
            icon,
            multiline = false,
            rows = 1,
            maxLength,
        },
        ref
    ) => {
        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            onChange?.(event.target.value);
        };

        const handleClear = () => {
            if (onChange) onChange('');
        };

        return (
            <StyledTextField
                ref={ref}
                name={name}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                value={value}
                fullWidth
                multiline={multiline}
                rows={rows}
                inputProps={{ maxLength }}
                slotProps={{
                    input: {
                        sx: {
                            background: '#fff',
                        },
                        startAdornment: icon && IconComponentMapper[icon] && (
                            <InputAdornment position="start">{IconComponentMapper[icon]}</InputAdornment>
                        ),
                        endAdornment: value.length > 0 && !multiline && (
                            <InputAdornment position="end">
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    onClick={handleClear}
                                    width={19}
                                    height={47}
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Backspace />
                                </Box>
                            </InputAdornment>
                        ),
                    },
                }}
            />
        );
    }
);

export default CustomInputField;
